import { Component } from '@theme/component';

/**
 * A custom element that renders an image gallery with thumbnails.
 * 
 * @extends Component
 */
export class ImageGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#init();
    this.#bindEvents();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Initialize the gallery by collecting images and creating the UI
   */
  #init() {
    const sourceImages = this.#getSourceImages();
    
    if (sourceImages.length === 0) {
      this.#showPlaceholder();
      return;
    }

    this.#createMainImage(sourceImages);
    this.#createThumbnails(sourceImages);
    this.#selectImage(0);
  }

  /**
   * Get all source images from hidden gallery-image blocks
   */
  #getSourceImages() {
    const sourceContainer = this.querySelector('[data-gallery-sources]');
    if (!sourceContainer) return [];

    const galleryImages = sourceContainer.querySelectorAll('[data-gallery-image]');
    return Array.from(galleryImages).map((img, index) => ({
      src: img.src,
      alt: img.alt,
      width: img.width,
      height: img.height,
      index
    }));
  }

  /**
   * Create the main image display area
   */
  #createMainImage(images) {
    const mainContainer = this.querySelector('[data-gallery-main]');
    if (!mainContainer) return;

    mainContainer.innerHTML = '';

    // Create all images but only show the first one without animation
    images.forEach((imageData, index) => {
      const img = document.createElement('img');
      img.className = `image-gallery__main-image${index === 0 ? ' image-gallery__main-image--active' : ''}`;
      img.src = imageData.src;
      img.alt = imageData.alt;
      img.width = imageData.width;
      img.height = imageData.height;
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.dataset.galleryIndex = index;

      // Set initial state for non-active images
      if (index !== 0) {
        img.style.transform = 'scale(0.3) translateX(100%)';
        img.style.opacity = '0';
      }

      mainContainer.appendChild(img);
    });
  }

  /**
   * Create thumbnail buttons
   */
  #createThumbnails(images) {
    const thumbContainer = this.querySelector('[data-gallery-thumbnails]');
    if (!thumbContainer || images.length <= 1) return;

    thumbContainer.innerHTML = '';

    images.forEach((imageData, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `image-gallery__thumbnail${index === 0 ? ' image-gallery__thumbnail--active' : ''}`;
      button.dataset.galleryIndex = index;
      button.setAttribute('aria-label', `Selecionar imagem ${index + 1}`);

      const img = document.createElement('img');
      img.src = this.#getThumbnailUrl(imageData.src);
      img.alt = imageData.alt;
      img.width = 200;
      img.height = 200;
      img.loading = 'lazy';

      button.appendChild(img);
      thumbContainer.appendChild(button);
    });

    // Ajusta o tamanho das miniaturas para preencher a altura disponível
    this.#adjustThumbnailSizes(images.length);
  }

  /**
   * Adjust thumbnail container size to maintain aspect ratios
   */
  #adjustThumbnailSizes(imageCount) {
    if (imageCount <= 1) return;

    const thumbContainer = this.querySelector('[data-gallery-thumbnails]');
    const mainContainer = this.querySelector('[data-gallery-main]');
    
    if (!thumbContainer || !mainContainer) return;

    // Aguarda o próximo frame para garantir que o layout foi calculado
    requestAnimationFrame(() => {
      if (window.innerWidth < 750) {
        // Reset estilos dinâmicos no mobile
        this.#resetMobileStyles();
        return;
      }

      const galleryContainer = this;
      const galleryWidth = galleryContainer.offsetWidth;
      const gap = parseInt(getComputedStyle(this).getPropertyValue('--gallery-gap')) || 12;
      const thumbnailAspectRatio = parseFloat(getComputedStyle(this).getPropertyValue('--thumbnail-aspect-ratio')) || 1;
      const mainAspectRatio = parseFloat(getComputedStyle(this).getPropertyValue('--main-image-aspect-ratio')) || 1;
      
      // Calcula a largura ideal das miniaturas baseada na altura que elas terão
      // Primeiro, estima a altura da imagem principal baseada na largura disponível
      const availableWidthForMain = galleryWidth * 0.75; // Reserva ~25% para miniaturas inicialmente
      const estimatedMainHeight = availableWidthForMain / mainAspectRatio;
      
      // Calcula a altura ideal para cada miniatura
      const totalGapHeight = gap * (imageCount - 1);
      const availableHeightForThumbnails = estimatedMainHeight - totalGapHeight;
      const thumbnailHeight = Math.max(60, availableHeightForThumbnails / imageCount);
      
      // Calcula a largura necessária baseada no aspect-ratio
      const thumbnailWidth = thumbnailHeight * thumbnailAspectRatio;
      
      // Ajusta a distribuição do espaço
      const thumbnailContainerWidth = thumbnailWidth;
      const actualMainWidth = galleryWidth - thumbnailContainerWidth - gap;
      
      // Aplica os tamanhos calculados
      thumbContainer.style.width = `${thumbnailContainerWidth}px`;
      mainContainer.style.width = `${actualMainWidth}px`;
      
      // Adiciona CSS dinâmico se não existir
      if (!document.getElementById('gallery-dynamic-styles')) {
        const style = document.createElement('style');
        style.id = 'gallery-dynamic-styles';
        style.textContent = `
          @media screen and (min-width: 750px) {
            .image-gallery__thumbnails[style*="width"] .image-gallery__thumbnail {
              height: auto;
              width: 100%;
            }
            .image-gallery__main[style*="width"] {
              flex: none;
            }
          }
        `;
        document.head.appendChild(style);
      }
    });
  }

  /**
   * Get thumbnail URL with proper sizing
   */
  #getThumbnailUrl(originalSrc) {
    // Simple implementation - in production you might want to use Shopify's image transformation
    return originalSrc.replace(/\.(jpg|jpeg|png|webp)/i, '_200x200.$1');
  }

  /**
   * Show placeholder when no images are available
   */
  #showPlaceholder() {
    const mainContainer = this.querySelector('[data-gallery-main]');
    if (!mainContainer) return;

    mainContainer.innerHTML = `
      <div class="image-gallery__placeholder">
        <p>Adicione imagens à galeria</p>
      </div>
    `;
  }

  /**
   * Bind events for thumbnail clicks and keyboard navigation
   */
  #bindEvents() {
    const { signal } = this.#controller;
    
    // Handle thumbnail clicks
    this.addEventListener('click', this.#handleThumbnailClick, { signal });
    
    // Handle keyboard navigation
    this.addEventListener('keydown', this.#handleKeyboardNavigation, { signal });
    
    // Handle window resize to recalculate thumbnail sizes
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  /**
   * Handle window resize events
   */
  #handleResize = () => {
    const images = this.#getSourceImages();
    if (images.length > 1) {
      this.#adjustThumbnailSizes(images.length);
    }
  }

  /**
   * Reset dynamic styles for mobile
   */
  #resetMobileStyles() {
    const thumbContainer = this.querySelector('[data-gallery-thumbnails]');
    const mainContainer = this.querySelector('[data-gallery-main]');

    if (thumbContainer) {
      thumbContainer.style.width = '';
    }
    if (mainContainer) {
      mainContainer.style.width = '';
    }
  }

  /**
   * Handle thumbnail click events
   * @param {Event} event - The click event
   */
  #handleThumbnailClick = (event) => {
    const thumbnail = event.target.closest('.image-gallery__thumbnail');
    if (!thumbnail) return;

    event.preventDefault();
    
    const index = parseInt(thumbnail.dataset.galleryIndex, 10);
    this.selectImage(index);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyboardNavigation = (event) => {
    const focusedThumbnail = event.target.closest('.image-gallery__thumbnail');
    if (!focusedThumbnail) return;

    const thumbnails = this.thumbnails;
    const currentIndex = Array.from(thumbnails).indexOf(focusedThumbnail);
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        newIndex = currentIndex > 0 ? currentIndex - 1 : thumbnails.length - 1;
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        newIndex = currentIndex < thumbnails.length - 1 ? currentIndex + 1 : 0;
        event.preventDefault();
        break;
      case 'Home':
        newIndex = 0;
        event.preventDefault();
        break;
      case 'End':
        newIndex = thumbnails.length - 1;
        event.preventDefault();
        break;
      default:
        return;
    }

    thumbnails[newIndex]?.focus();
    this.selectImage(newIndex);
  }

  /**
   * Select an image by index - public method
   * @param {number} index - The index of the image to select
   */
  selectImage(index) {
    this.#selectImage(index);
  }

  /**
   * Select an image by index - private implementation
   * @param {number} index - The index of the image to select
   */
  #selectImage(index) {
    const mainImages = this.mainImages;
    const thumbnails = this.thumbnails;

    if (index < 0 || index >= mainImages.length) return;

    // Find current active image
    const currentActiveImage = this.querySelector('.image-gallery__main-image--active');
    const newActiveImage = mainImages[index];

    // If clicking on the same image that's already active, just update thumbnails and return
    if (currentActiveImage === newActiveImage) {
      thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('image-gallery__thumbnail--active', i === index);
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      return;
    }

    if (currentActiveImage) {
      // Animate out the current image
      this.#animateImageOut(currentActiveImage);
    }

    // Update thumbnails immediately
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('image-gallery__thumbnail--active', i === index);
      thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    // Animate in the new image
    this.#animateImageIn(newActiveImage);

    // Announce change to screen readers
    this.#announceImageChange(index + 1, mainImages.length);
  }

  /**
   * Animate out the current active image
   * @param {HTMLImageElement} image - The image to animate out
   */
  #animateImageOut(image) {
    // Remove active class immediately
    image.classList.remove('image-gallery__main-image--active');

    // Set initial state for exit animation
    image.style.transform = 'scale(1) translateX(0)';
    image.style.opacity = '1';

    // Force reflow
    image.offsetHeight;

    // Set exit animation
    image.style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
    image.style.transform = 'scale(0.3) translateX(-100%)';
    image.style.opacity = '0';
  }

  /**
   * Animate in the new active image
   * @param {HTMLImageElement} image - The image to animate in
   */
  #animateImageIn(image) {
    // Reset any existing transform properties first
    this.#resetImageTransform(image);

    // Set initial state for entrance animation
    image.style.transform = 'scale(0.3) translateX(100%)';
    image.style.opacity = '0';
    image.classList.add('image-gallery__main-image--active');

    // Force reflow
    image.offsetHeight;

    // Set entrance animation
    image.style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
    image.style.transform = 'scale(1) translateX(0)';
    image.style.opacity = '1';
  }

  /**
   * Reset image transform properties
   * @param {HTMLImageElement} image - The image to reset
   */
  #resetImageTransform(image) {
    image.style.transition = 'none';
    image.style.transform = 'scale(1) translateX(0)';
    image.style.opacity = '1';

    // Force reflow to apply the reset
    image.offsetHeight;

    // Re-enable transitions
    image.style.transition = '';
  }

  /**
   * Announce image change to screen readers
   * @param {number} current - Current image number (1-based)
   * @param {number} total - Total number of images
   */
  #announceImageChange(current, total) {
    const announcement = `Imagem ${current} de ${total} selecionada`;
    
    // Create or update live region for announcements
    let liveRegion = this.querySelector('.sr-only[aria-live]');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      this.appendChild(liveRegion);
    }
    
    liveRegion.textContent = announcement;
  }

  /**
   * Get all main images
   * @returns {NodeList} List of main image elements
   */
  get mainImages() {
    return this.querySelectorAll('.image-gallery__main-image');
  }

  /**
   * Get all thumbnail buttons
   * @returns {NodeList} List of thumbnail button elements
   */
  get thumbnails() {
    return this.querySelectorAll('.image-gallery__thumbnail');
  }

  /**
   * Get the currently active image index
   * @returns {number} The index of the active image
   */
  get activeIndex() {
    const activeImage = this.querySelector('.image-gallery__main-image--active');
    return activeImage ? parseInt(activeImage.dataset.galleryIndex, 10) : 0;
  }

  /**
   * Get the total number of images
   * @returns {number} Total number of images
   */
  get imageCount() {
    return this.mainImages.length;
  }
}

// Register the custom element
if (!customElements.get('image-gallery')) {
  customElements.define('image-gallery', ImageGallery);
}